import { useCallback } from 'react';

import type { ResultStatus } from 'types/imageRecognition';
import type { SelfReportedResult } from 'types/models';
import type { TestType } from 'types/testType';
import { HMO } from 'constants/hmo';
import { detectHmo } from 'utils/hmoDetection';
import { clearLeumitToken, getLeumitToken } from 'services/leumitSession';
import { getQrScanResponse } from 'services/models';
import {
  resolveSessionResult,
  sendSessionEndToParent,
  SESSION_RESULT_LABEL_MAP,
  SessionResultStatus,
  type SessionEndResponse,
} from 'services/leumitPostMessage';

export type SendSessionEndParams = {
  testType: TestType;
  modelResult?: ResultStatus | null;
  selfReportedResult?: SelfReportedResult;
  forceInvalid?: boolean;
};

export type SendSessionEndResult = {
  didSend: boolean;
  isExpired: boolean;
};

/**
 * Builds and posts SessionEndResponse to the Leumit parent iframe (Leumit HMO only).
 * Skips gracefully when not embedded / no token / missing origin.
 */
const useLeumitSessionEnd = () => {
  const sendSessionEnd = useCallback(
    async (params: SendSessionEndParams): Promise<SendSessionEndResult> => {
      if (detectHmo() !== HMO.LEUMIT) {
        return { didSend: false, isExpired: false };
      }

      const token = getLeumitToken();
      if (!token) {
        return { didSend: false, isExpired: false };
      }

      const qrScanResponse = await getQrScanResponse();
      const uniqueId = qrScanResponse?.uniqueId ?? '';
      const testExpireDate = qrScanResponse?.testExpireDate ?? '';

      let result: SessionEndResponse['result'];
      let status: SessionEndResponse['status'];
      let isExpired = false;

      if (params.forceInvalid) {
        status = SessionResultStatus.INVALID;
        result = SESSION_RESULT_LABEL_MAP[SessionResultStatus.INVALID];
      } else {
        const selfReportedResult = params.selfReportedResult ?? 'error';
        const resolved = resolveSessionResult(
          params.modelResult,
          selfReportedResult,
          testExpireDate,
        );
        result = resolved.result;
        status = resolved.status;
        isExpired = resolved.isExpired;
      }

      const payload: SessionEndResponse = {
        testType: params.testType,
        token,
        result,
        uniqueId,
        testExpireDate,
        status,
      };

      const didSend = sendSessionEndToParent(payload);
      if (didSend) {
        clearLeumitToken();
      }

      return { didSend, isExpired };
    },
    [],
  );

  return { sendSessionEnd };
};

export default useLeumitSessionEnd;
